import axios from 'axios';
import secureStorage from '@/utils/secureStorage';
import { API } from '@/config/api';

const state = {
    articles: [],
    searchResults: [],
    aiProcessedArticle: null,
    loading: false,
    pagination: {
        currentPage: 1,
        lastPage: 1,
        perPage: 10,
        total: 0
    }
};

const getters = {
    draftArticles: (state) => state.articles.filter(a => a.status === 'draft'),
    publishedArticles: (state) => state.articles.filter(a => a.status === 'published'),
    archivedArticles: (state) => state.articles.filter(a => a.status === 'archived')
};

const mutations = {
    SET_ARTICLES(state, articles) {
        state.articles = articles;
    },
    APPEND_ARTICLES(state, articles) {
        state.articles = [...state.articles, ...articles];
    },
    ADD_ARTICLE(state, article) {
        state.articles.unshift(article);
    },
    SET_SEARCH_RESULTS(state, results) {
        state.searchResults = results;
    },
    SET_AI_PROCESSED_ARTICLE(state, article) {
        state.aiProcessedArticle = article;
    },
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_PAGINATION(state, pagination) {
        state.pagination = pagination;
    },
    UPDATE_ARTICLE(state, updatedArticle) {
        const index = state.articles.findIndex(a => a.id === updatedArticle.id);
        if (index !== -1) {
            state.articles.splice(index, 1, updatedArticle);
        }
    },
    DELETE_ARTICLE(state, articleId) {
        state.articles = state.articles.filter(a => a.id !== articleId);
    },
    CLEAR_SEARCH_RESULTS(state) {
        state.searchResults = [];
    },
    CLEAR_AI_PROCESSED_ARTICLE(state) {
        state.aiProcessedArticle = null;
    }
};

const actions = {
    async searchArticles({ commit }, filters) {
        commit('SET_LOADING', true);
        try {
            const user = secureStorage.getItem('user');
            const formData = new FormData();
            formData.append('category', filters.category);
            formData.append('region', filters.region);
            formData.append('start_date', filters.dateFrom);
            formData.append('end_date', filters.dateTo);
            formData.append('language', filters.language);
            formData.append('user_id', user?.id);
            formData.append('organization_id', user?.company_id);
            formData.append('async', 'false');

            const config = {
                headers: {
                    "Content-Type": "multipart/form-data",
                }
            };

            const response = await axios.post(
                API.ARTICLE_GENERATE_TOPICS,
                formData,
                config
            );

            // Transform topics to article format for display
            const articles = response.data.topics ? response.data.topics.map(topic => ({
                id: topic.normalized_name,
                title: topic.topic_name,
                subtitle: topic.summary,
                slug: topic.normalized_name,
                dossier_url: topic.dossier_url,
                category: filters.category,
                region: filters.region,
                language: filters.language,
                created_at: new Date().toISOString()
            })) : [];

            commit('SET_SEARCH_RESULTS', articles);
            return { articles };
        } catch (error) {
            console.error('Search articles failed:', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async processArticleWithAI({ commit }, article) {
        commit('SET_LOADING', true);
        try {
            const user = secureStorage.getItem('user');
            const formData = new FormData();
            formData.append('slug', article.slug);
            formData.append('language', article.language);
            formData.append('user_id', user?.id);
            formData.append('organization_id', user?.company_id);
            formData.append('async', 'true');

            const config = {
                headers: {
                    "Content-Type": "multipart/form-data",
                }
            };

            const response = await axios.post(
                API.ARTICLE_GENERATE_FROM_TOPIC,
                formData,
                config
            );

            // Članak će biti dodat preko WebSocket-a kada backend kreira zapis u bazi
            // Ne dodajemo ga lokalno jer nema pravi ID
            commit('SET_AI_PROCESSED_ARTICLE', response.data.article);
            return { article: response.data.article };
        } catch (error) {
            console.error('AI processing failed:', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async getArticles({ commit }, { page = 1, perPage = 10 } = {}) {
        commit('SET_LOADING', true);
        try {
            const user = secureStorage.getItem('user');
            const response = await axios.get(API.ARTICLES_GET_ALL, {
                params: {
                    token: 'test',
                    user_id: user?.id,
                    organization_id: user?.company_id,
                    per_page: perPage,
                    page: page
                }
            });

            const articlesData = response.data.articles;

            if (page === 1) {
                commit('SET_ARTICLES', articlesData.data || []);
            } else {
                commit('APPEND_ARTICLES', articlesData.data || []);
            }

            commit('SET_PAGINATION', {
                currentPage: articlesData.current_page,
                lastPage: articlesData.last_page,
                perPage: articlesData.per_page,
                total: articlesData.total
            });

            return { articles: articlesData.data || [] };
        } catch (error) {
            console.error('Get articles failed:', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async getArticle({ commit }, articleId) {
        commit('SET_LOADING', true);
        try {
            const user = secureStorage.getItem('user');
            const response = await axios.get(API.ARTICLE_GET_ONE(articleId), {
                params: {
                    token: 'test',
                    user_id: user?.id
                }
            });

            return response.data.article;
        } catch (error) {
            console.error('Get article failed:', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async updateArticle({ commit }, { id, data }) {
        commit('SET_LOADING', true);
        try {
            const user = secureStorage.getItem('user');
            const response = await axios.post(API.ARTICLE_UPDATE(id), {
                token: 'test',
                user_id: user?.id,
                ...data
            });

            commit('UPDATE_ARTICLE', response.data.article);
            return response.data;
        } catch (error) {
            console.error('Update article failed:', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async deleteArticle({ commit }, articleId) {
        commit('SET_LOADING', true);
        try {
            const user = secureStorage.getItem('user');
            await axios.delete(API.ARTICLE_DELETE(articleId), {
                data: {
                    token: 'test',
                    user_id: user?.id
                }
            });

            commit('DELETE_ARTICLE', articleId);
        } catch (error) {
            console.error('Delete article failed:', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async restoreArticle({ commit, dispatch }, articleId) {
        commit('SET_LOADING', true);
        try {
            // Mock restore
            dispatch('getArticles', { page: 1 });
        } catch (error) {
            console.error('Restore article failed:', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    async getArticlesByTranscription({ commit }, transcriptionId) {
        commit('SET_LOADING', true);
        try {
            // Mock data
            return [];
        } catch (error) {
            console.error('Get articles by transcription failed:', error);
            throw error;
        } finally {
            commit('SET_LOADING', false);
        }
    },

    clearSearchResults({ commit }) {
        commit('CLEAR_SEARCH_RESULTS');
    },

    clearAIProcessedArticle({ commit }) {
        commit('CLEAR_AI_PROCESSED_ARTICLE');
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
